<script setup lang="ts">
import { chartColors } from '@forgd/ui-kit/charts/lib/colors'
import { VisArea, VisLine, VisXYContainer } from '@unovis/vue'

// value can be number or string, to handle iso dates as xAxis
type Datum = Record<string, number | string>

interface Props {
  data: Datum[]
  xAxis?: keyof Datum
  yAxis?: keyof Datum
  width?: string | number
  height?: string | number
  color?: string
  fillOpacity?: number
}

/**
 * ChartSparkline
 *
 * Bare Unovis line chart to visualize chart data as a sparkline
 *
 * - attempts to guess axes
 * - handles string time axis
 */
const props = withDefaults(defineProps<Props>(), {
  width: 60,
  height: 30,
  fillOpacity: 0,
})

const effectiveXAxis = computed(() => {
  if (props.xAxis) {
    return props.xAxis
  }
  const d = props.data[0]
  if (!d) {
    return 'x'
  }

  for (const key of ['x', 'index', 'timestamp', 'date']) {
    if (key in d) {
      return key
    }
  }
  return Object.keys(d)[0] ?? 'x'
})

const effectiveYAxis = computed(() => {
  if (props.yAxis) {
    return props.yAxis
  }
  const d = props.data[0]
  if (!d) {
    return 'y'
  }

  for (const key of ['y', 'value']) {
    if (key in d) {
      return key
    }
  }
  return Object.keys(d)[1] || Object.keys(d)[0] || 'y'
})

const trend = computed<-1 | 0 | 1>(() => {
  if (!props.data.length) {
    return 0
  }
  const firstItem = props.data.at(0)
  const lastItem = props.data.at(-1)
  if (!firstItem || !lastItem) {
    return 0
  }

  const first = firstItem[effectiveYAxis.value]
  const last = lastItem[effectiveYAxis.value]
  const value = Number(last) - Number(first)
  return value > 0
    ? 1
    : value < 0
      ? -1
      : 0
})

const color = computed(() => {
  if (props.color) {
    return props.color
  }
  const key = trend.value
  return [
    chartColors.trend[key],
  ]
})

function getX(datum: Datum, index: number): number {
  const value = datum[effectiveXAxis.value]
  return typeof value === 'number'
    ? value
    : index
}

function getY(datum: Datum): number {
  return Number(datum[effectiveYAxis.value])
}
</script>

<template>
  <div data-ui="ChartSparkline">
    <VisXYContainer :data="props.data" :width="props.width" :height="props.height">
      <VisArea
        v-if="props.fillOpacity > 0"
        :x="getX"
        :y="getY"
        :color="color"
        :opacity="props.fillOpacity"
        duration="0"
      />
      <VisLine
        :x="getX"
        :y="getY"
        :color="color"
        duration="0"
        line-width="1.5"
      />
    </VisXYContainer>
  </div>
</template>

<style>
div[data-ui="ChartSparkline"] {
  /* hide the "helper" line */
  path[class*="lineSelectionHelper"] {
    display: none !important;
  }
}
</style>
